import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { api, type Note, type Attachment } from '../lib/api.ts'
import { 
  ArrowLeft, 
  Save, 
  Loader2, 
  Sparkles, 
  Upload, 
  File, 
  Trash2,
  Download,
  MessageSquare,
  Send,
  Image,
  FileText,
  FileSpreadsheet,
  FileCode,
  FileAudio,
  FileVideo,
  FileArchive
} from 'lucide-react'

function NoteEditor() {
  const { id } = useParams<{ id?: string }>()
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  const isNew = !id
  const [note, setNote] = useState<Note | null>(null)
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [summarizing, setSummarizing] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [question, setQuestion] = useState('')
  const [asking, setAsking] = useState(false)
  const [aiResponse, setAiResponse] = useState('')
  const [error, setError] = useState('')

  const fetchNote = useCallback(async () => {
    if (!id) return
    
    try {
      const data = await api.getNote(id)
      setNote(data)
      setTitle(data.title)
      setContent(data.content)
      setAttachments(data.attachments)
    } catch (err) {
      setError('Failed to load note')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchNote()
  }, [fetchNote])

  const handleSave = async (options?: { generateSummary?: boolean }) => {
    if (!title.trim()) {
      setError('Title is required')
      return
    }

    setError('')
    setSaving(true)
    setSummarizing(options?.generateSummary || false)

    try {
      if (isNew) {
        const newNote = await api.createNote(title, content, options?.generateSummary)
        navigate(`/notes/${newNote.id}`, { replace: true })
      } else {
        const updated = await api.updateNote(id!, { title, content }, options?.generateSummary)
        setNote(updated)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save note')
    } finally {
      setSaving(false)
      setSummarizing(false)
    }
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !id) return

    setUploading(true)
    try {
      const attachment = await api.uploadFile(id, file)
      setAttachments([...attachments, attachment])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload file')
    } finally {
      setUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!confirm('Delete this file?')) return

    try {
      await api.deleteFile(attachmentId)
      setAttachments(attachments.filter(a => a.id !== attachmentId))
    } catch (err) {
      setError('Failed to delete file')
    }
  }

  const handleAskAI = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!question.trim() || !id) return

    setAsking(true)
    setAiResponse('')
    try {
      const answer = await api.askAI(id, question)
      setAiResponse(answer)
    } catch (err) {
      setAiResponse('Sorry, I could not answer your question.')
    } finally {
      setAsking(false)
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  // Helper to check if file is an image
  const isImage = (contentType: string) => contentType.startsWith('image/')

  // Helper to get file icon based on content type
  const getFileIcon = (contentType: string, filename: string) => {
    if (contentType.startsWith('image/')) return <Image className="w-6 h-6 text-purple-500" />
    if (contentType.includes('pdf')) return <FileText className="w-6 h-6 text-red-500" />
    if (contentType.includes('word') || contentType.includes('document') || filename.endsWith('.doc') || filename.endsWith('.docx')) {
      return <FileText className="w-6 h-6 text-blue-500" />
    }
    if (contentType.includes('excel') || contentType.includes('sheet') || contentType.includes('csv') || filename.endsWith('.xls') || filename.endsWith('.xlsx')) {
      return <FileSpreadsheet className="w-6 h-6 text-green-500" />
    }
    if (contentType.includes('code') || contentType.includes('json') || contentType.includes('javascript') || contentType.includes('typescript') || contentType.includes('html') || contentType.includes('css') || /\.(js|ts|jsx|tsx|json|html|css|py|java|go|rs|php)$/.test(filename)) {
      return <FileCode className="w-6 h-6 text-orange-500" />
    }
    if (contentType.startsWith('audio/')) return <FileAudio className="w-6 h-6 text-pink-500" />
    if (contentType.startsWith('video/')) return <FileVideo className="w-6 h-6 text-red-600" />
    if (contentType.includes('zip') || contentType.includes('archive') || contentType.includes('compressed') || /\.(zip|rar|7z|tar|gz|bz2)$/.test(filename)) {
      return <FileArchive className="w-6 h-6 text-yellow-600" />
    }
    return <File className="w-6 h-6 text-gray-400" />
  }

  // Helper to get file extension
  const getFileExtension = (filename: string) => {
    const parts = filename.split('.')
    return parts.length > 1 ? parts.pop()?.toUpperCase() : ''
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link
          to="/notes"
          className="btn-secondary"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Link>
        
        <div className="flex-1" />
        
        <button
          onClick={() => handleSave({ generateSummary: false })}
          disabled={saving}
          className="btn-primary"
        >
          {saving && !summarizing ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main editor */}
        <div className="lg:col-span-2 space-y-6">
          {/* Title */}
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Note title"
            className="input text-xl font-semibold"
          />

          {/* Content */}
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Start writing..."
            rows={20}
            className="input font-mono resize-none"
          />

          {/* AI Summary */}
          {note?.summary && (
            <div className="card p-4 bg-primary-50 border-primary-200">
              <div className="flex items-center gap-2 text-primary-700 mb-2">
                <Sparkles className="w-4 h-4" />
                <span className="font-medium">AI Summary</span>
              </div>
              <p className="text-sm text-gray-700">{note.summary}</p>
            </div>
          )}

          {/* Attachments */}
          {!isNew && (
            <div className="card p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-gray-900">
                  Attachments ({attachments.length})
                </h3>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="btn-secondary text-sm"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Upload
                </button>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileUpload}
                className="hidden"
              />

              {attachments.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  No attachments yet
                </p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {attachments.map((attachment) => {
                    const fileUrl = api.getFileUrl(attachment.id)
                    const showImage = isImage(attachment.content_type)
                    const ext = getFileExtension(attachment.filename)
                    
                    return (
                      <div
                        key={attachment.id}
                        className="group relative bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                      >
                        {/* Thumbnail / Preview Area */}
                        <div className="aspect-square bg-gray-50 flex items-center justify-center overflow-hidden">
                          {showImage ? (
                            <img
                              src={fileUrl}
                              alt={attachment.filename}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              onError={(e) => {
                                // If image fails to load, show icon instead
                                e.currentTarget.style.display = 'none'
                              }}
                            />
                          ) : (
                            <div className="flex flex-col items-center justify-center p-4">
                              {getFileIcon(attachment.content_type, attachment.filename)}
                              {ext && (
                                <span className="mt-2 text-xs font-bold text-gray-400 uppercase">
                                  {ext}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* File Info Overlay */}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 pt-8 opacity-0 group-hover:opacity-100 transition-opacity">
                          <p className="text-sm font-medium text-white truncate">
                            {attachment.filename}
                          </p>
                          <p className="text-xs text-white/80">
                            {formatFileSize(attachment.size)}
                          </p>
                        </div>
                        
                        {/* Always visible filename */}
                        <div className="p-2 bg-white border-t border-gray-100">
                          <p className="text-xs font-medium text-gray-700 truncate">
                            {attachment.filename}
                          </p>
                          <p className="text-xs text-gray-400">
                            {formatFileSize(attachment.size)}
                          </p>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <a
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 bg-white/90 hover:bg-white rounded-md shadow-sm text-gray-600 hover:text-primary-600 transition-colors"
                            title={showImage ? 'View / Download' : 'Download'}
                          >
                            <Download className="w-3.5 h-3.5" />
                          </a>
                          <button
                            onClick={() => handleDeleteAttachment(attachment.id)}
                            className="p-1.5 bg-white/90 hover:bg-white rounded-md shadow-sm text-gray-600 hover:text-red-600 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* AI Actions */}
          <div className="card p-4">
            <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary-600" />
              AI Actions
            </h3>
            
            <button
              onClick={() => handleSave({ generateSummary: true })}
              disabled={saving || content.length < 50}
              className="btn-secondary w-full mb-3 text-sm"
            >
              {summarizing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Summary
                </>
              )}
            </button>
            
            {content.length < 50 && (
              <p className="text-xs text-gray-500">
                Write at least 50 characters to generate a summary
              </p>
            )}
          </div>

          {/* Ask AI */}
          {!isNew && (
            <div className="card p-4">
              <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-primary-600" />
                Ask About This Note
              </h3>
              
              <form onSubmit={handleAskAI} className="space-y-3">
                <input
                  type="text"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Ask a question..."
                  className="input text-sm"
                />
                <button
                  type="submit"
                  disabled={asking || !question.trim()}
                  className="btn-primary w-full text-sm"
                >
                  {asking ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Thinking...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Ask AI
                    </>
                  )}
                </button>
              </form>

              {aiResponse && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700">{aiResponse}</p>
                </div>
              )}
            </div>
          )}

          {/* Info */}
          {note && (
            <div className="card p-4 text-sm text-gray-600">
              <p className="mb-2">
                <span className="font-medium">Created:</span>{' '}
                {new Date(note.created_at).toLocaleString()}
              </p>
              <p>
                <span className="font-medium">Updated:</span>{' '}
                {new Date(note.updated_at).toLocaleString()}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default NoteEditor
